import React, { Component } from "react";
import { PageTitle, LoadingSpinner, DeleteConfirmationModal, NotificationPopup, AddToCartModal, Pagination } from "../../common";
import { ModalAddProduct, ModalEditProduct } from "../../components";
import { useApiRequests } from "../../services";
import { CartContext } from "../../CartContext";
import "./styles.scss";

const isShoppingCartEnabled = process.env.REACT_APP_FEATURE_SHOPPING_CART === 'true';

// Helper component for individual product card with truncation logic
const ProductCardItem = ({
  product,
  userType,
  onEditProduct,
  onDeleteProduct,
  onAddToCart,
  cartItems = [],
}) => {
  const [productImages, setProductImages] = React.useState([]);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [isLoadingImages, setIsLoadingImages] = React.useState(false);
  const [isZoomModalOpen, setIsZoomModalOpen] = React.useState(false);
  const [zoomImageIndex, setZoomImageIndex] = React.useState(0);

  // Load product images when component mounts
  React.useEffect(() => {
    loadProductImages();
  }, [product.id]);

  // Keyboard navigation for zoom modal
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isZoomModalOpen) return;

      switch (event.key) {
        case 'Escape':
          closeZoomModal();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          navigateZoomImage('previous');
          break;
        case 'ArrowRight':
          event.preventDefault();
          navigateZoomImage('next');
          break;
        default:
          break;
      }
    };

    if (isZoomModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isZoomModalOpen, zoomImageIndex, productImages.length]);

  const loadProductImages = async () => {
    try {
      setIsLoadingImages(true);
      const apiRequests = useApiRequests();

      // Get metadata for all images for this product
      const metadata = await apiRequests.getAllFilesAsMetadata(product.id);
      
      if (metadata && metadata.length > 0) {
        // Download all images
        const imagePromises = metadata.map(async (imageInfo) => {
          try {
            const blobName = imageInfo.name;
            const downloadedImageUrl = await apiRequests.downloadFile(blobName);
            return {
              url: downloadedImageUrl,
              name: imageInfo.name,
              size: imageInfo.size,
              metadata: imageInfo
            };
          } catch (error) {
            console.error('Error downloading image:', imageInfo.name, error);
            return null;
          }
        });

        const downloadedImages = await Promise.all(imagePromises);
        const validImages = downloadedImages.filter(img => img !== null);
        
        setProductImages(validImages);
        setCurrentImageIndex(0);
      } else {
        setProductImages([]);
      }
    } catch (error) {
      console.error('Error loading product images:', error);
      setProductImages([]);
    } finally {
      setIsLoadingImages(false);
    }
  };

  // Cleanup blob URLs when component unmounts
  React.useEffect(() => {
    return () => {
      productImages.forEach(img => {
        if (img.url && img.url.startsWith('blob:')) {
          URL.revokeObjectURL(img.url);
        }
      });
    };
  }, [productImages]);

  // Image navigation
  const navigateImage = (direction) => {
    if (productImages.length === 0) return;

    let newIndex;
    if (direction === 'previous') {
      newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : productImages.length - 1;
    } else if (direction === 'next') {
      newIndex = currentImageIndex < productImages.length - 1 ? currentImageIndex + 1 : 0;
    }
    setCurrentImageIndex(newIndex);
  };

  // Zoom modal functions
  const openZoomModal = (imageIndex = currentImageIndex) => {
    setZoomImageIndex(imageIndex);
    setIsZoomModalOpen(true);
  };

  const closeZoomModal = () => {
    setIsZoomModalOpen(false);
  };

  const navigateZoomImage = (direction) => {
    if (productImages.length === 0) return;

    let newIndex;
    if (direction === 'previous') {
      newIndex = zoomImageIndex > 0 ? zoomImageIndex - 1 : productImages.length - 1;
    } else if (direction === 'next') {
      newIndex = zoomImageIndex < productImages.length - 1 ? zoomImageIndex + 1 : 0;
    }
    setZoomImageIndex(newIndex);
  };
  

  const { id, name, comment, price, imageUrl, isAvailable } = product;
  const fullDescription = comment || "-";

  // Render image gallery
  const renderImageGallery = () => {
    if (isLoadingImages) {
      return (
        <div className="image-loading">
          <div className="loading-spinner">Loading images...</div>
        </div>
      );
    }

    if (productImages.length === 0) {
      return (
        <div className="no-images">
          <div className="no-image-placeholder">
            <span
              role="img"
              aria-label="No images available"
              style={{ fontSize: "2rem" }}
            >
              {/* You can replace this with an SVG or icon font if preferred */}
              📷
            </span>
            <p>No images available</p>
          </div>
        </div>
      );
    }

    const currentImage = productImages[currentImageIndex];
    return (
      <div className="product-image-gallery">
        <div className="image-container">
          <img
            src={currentImage.url}
            alt={`${name} - Image ${currentImageIndex + 1}`}
            className="product-image"
            onClick={() => openZoomModal(currentImageIndex)}
            style={{ cursor: 'pointer' }}
            title="Click to zoom"
          />
          
          {productImages.length > 1 && (
            <>
              <button
                className="nav-arrow nav-arrow-left"
                onClick={() => navigateImage('previous')}
              >
                ‹
              </button>
              <button
                className="nav-arrow nav-arrow-right"
                onClick={() => navigateImage('next')}
              >
                ›
              </button>
              <div className="image-counter">
                {currentImageIndex + 1} / {productImages.length}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Render zoom modal
  const renderZoomModal = () => {
    if (!isZoomModalOpen || productImages.length === 0) return null;

    const currentZoomImage = productImages[zoomImageIndex];
    
    return (
      <div className="zoom-modal-overlay" onClick={closeZoomModal}>
        <div className="zoom-modal-content" onClick={(e) => e.stopPropagation()}>
          <button
            className="zoom-close-btn"
            onClick={closeZoomModal}
            aria-label="Close image zoom"
          >
            ✕
          </button>
          
          <div className="zoom-image-container">
            <img
              src={currentZoomImage.url}
              alt={`${name} - Zoomed Image ${zoomImageIndex + 1}`}
              className="zoom-image"
            />
            
            {productImages.length > 1 && (
              <>
                <button
                  className="zoom-nav-arrow zoom-nav-left"
                  onClick={() => navigateZoomImage('previous')}
                >
                  ‹
                </button>
                <button
                  className="zoom-nav-arrow zoom-nav-right"
                  onClick={() => navigateZoomImage('next')}
                >
                  ›
                </button>
                <div className="zoom-image-counter">
                  {zoomImageIndex + 1} / {productImages.length}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="product-card p-0" key={id}>
      <div className="card-body">
        <h2 className="card-title pt-16" title={name}>{name}</h2>
      </div>
      
      {/* Image Gallery Section */}
      {renderImageGallery()}
      
      {/* Zoom Modal */}
      {renderZoomModal()}
      
      <div className="card-description-section">
        <p className="card-text-description" title={fullDescription}>
          <strong>Description:</strong>{" "}
          {fullDescription}
          <br />
          {imageUrl && (
            <a
              href={imageUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#445de9" }}
            >
              Link to image
            </a>
          )}
          {!isAvailable && (
            <div>
              <br />
              <span style={{ color: "red" }}>
                This product is not available
              </span>
            </div>
          )}
        </p>

        {userType === "1" && (
          <div className="management-icons">
            <span
              aria-hidden="true"
              className="vismaicon vismaicon-dynamic vismaicon-menu vismaicon-edit mr-8"
              onClick={() => onEditProduct(product)}
            ></span>
            {process.env.REACT_APP_FEATURE_DELETE_TYPE_VALUES_AND_PRODUCTS === "true" && (
              <span
                aria-hidden="true"
                className="vismaicon vismaicon-dynamic vismaicon-menu vismaicon-delete"
                onClick={() => onDeleteProduct(id)}
              ></span>
            )}
          </div>
        )}
                <p className="card-text-price pt-8 mb-0">
          <span className="pr-4">
            <b>Price:</b>
          </span>
          {price} kudos points
        </p>
      </div>
      
      <div className="card-actions">
        {isShoppingCartEnabled && isAvailable && (
          (cartItems ?? []).some((ci) => ci.productId === id) ? (
            <button type="button" className="btn btn-default" disabled>
              In cart
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onAddToCart(id)}
            >
              Add to cart
            </button>
          )
        )}
      </div>
    </div>
  );
};

export class Products extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      products: [],
      isDataLoading: true,
      isLoading: false,
      isDeleteLoading: false,
      selectedProduct: {},
      isEditModalOpen: false,
      isAddModalOpen: false,
      userType: localStorage.getItem("userType"),
      productIdToDelete: null,
      productNameToDelete: "",
      isDeleteModalOpen: false,
      availabilityFilter: "all", // "all", "available", "unavailable"
      isCancelPopupOpen: false,
      isSuccessPopupOpen: false,
      successMessage: "",
      // New delete flow state
      deletePreCheck: null,
      // Add-to-cart comment popup
      addToCartModal: { isOpen: false, productId: null, productName: "" },
      // Pagination state
      currentPage: 1,
      pageSize: 12,
    };

    this.fetchProducts = this.fetchProducts.bind(this);
    this.openProductEditModal = this.openProductEditModal.bind(this);
    this.openAddProductModal = this.openAddProductModal.bind(this);
    this.closeAllModals = this.closeAllModals.bind(this);
    this.deleteProduct = this.deleteProduct.bind(this);
    this.toggleDeleteModal = this.toggleDeleteModal.bind(this);
    this.handleAvailabilityFilterChange = this.handleAvailabilityFilterChange.bind(this);
    this.handleCancelAction = this.handleCancelAction.bind(this);
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.handleArchive = this.handleArchive.bind(this);
    this.handlePermanentDelete = this.handlePermanentDelete.bind(this);
  }

  async fetchProducts() {
    const { getAllProducts } = useApiRequests();
    const response = await getAllProducts();
    this.setState({
      products: response.data,
      isDataLoading: false,
      isLoading: false,
    });
  }

  // "Add to cart" opens a popup so the user can attach an optional comment.
  openAddToCartModal = (productId) => {
    const product = (this.state.products || []).find((p) => p.id === productId);
    this.setState({
      addToCartModal: {
        isOpen: true,
        productId,
        productName: product ? product.name : "",
      },
    });
  };

  closeAddToCartModal = () => {
    this.setState({
      addToCartModal: { isOpen: false, productId: null, productName: "" },
    });
  };

  confirmAddToCart = async (comment) => {
    const { addToCart } = useApiRequests();
    const { productId } = this.state.addToCartModal;
    this.closeAddToCartModal();
    try {
      await addToCart(productId, comment);
      await this.context.refreshCart();
    } catch (error) {
      // axios response interceptor already surfaces the error toast
    }
  };

  componentDidMount() {
    this.fetchProducts();
    // Cart refresh is handled by Header (rendered on the products route), so
    // calling it here too would trigger a redundant /cart fetch on first load.
  }

  openProductEditModal(product) {
    this.setState({
      isEditModalOpen: true,
      selectedProduct: product,
    });
  }

  openAddProductModal() {
    this.setState({
      isAddModalOpen: true,
    });
  }

  closeAllModals() {
    this.setState({
      isEditModalOpen: false,
      isAddModalOpen: false,
      isDeleteModalOpen: false,
      selectedProduct: {},
      productIdToDelete: null,
    });
  }

  deleteProduct = async () => {
    if (this.state.productIdToDelete) {
      try {
        const apiRequests = useApiRequests();
        const response = await apiRequests.deleteProductById(
          this.state.productIdToDelete
        );

        if (response.status === 200) {
          this.setState({ isLoading: true }, () => {
            setTimeout(() => {
              this.setState({
                isLoading: false,
                selectedProduct: {},
                productIdToDelete: null,
                isDeleteModalOpen: false,
              });
              this.fetchProducts();
            }, 200);
          });
        } else {
          // Handle error if needed
        }
      } catch (error) {
        console.error(error.message);
      }
    }
  };

  openDeleteProductModal = (id) => {
    this.setState({
      productIdToDelete: id,
      isDeleteModalOpen: true,
    });
  };

  toggleDeleteModal() {
    this.setState((prevState) => ({
      isDeleteModalOpen: !prevState.isDeleteModalOpen,
      productIdToDelete: null,
      deletePreCheck: null,
      productNameToDelete: "",
    }));
  }

  handleCancelAction() {
    this.setState({
      isDeleteModalOpen: false,
      productIdToDelete: null,
      deletePreCheck: null,
      productNameToDelete: "",
      isCancelPopupOpen: true
    });
    setTimeout(() => {
      this.setState({ isCancelPopupOpen: false });
    }, 10000);
  }

  // New delete flow: Pre-check before showing modal
  handleDeleteClick = async (productId) => {
    const apiRequests = useApiRequests();
    const product = this.state.products.find(p => p.id === productId);

    try {
      const response = await apiRequests.preCheckDeleteProduct(productId);
      const preCheckData = response.data;

      this.setState({
        deletePreCheck: preCheckData,
        productIdToDelete: productId,
        productNameToDelete: product ? product.name : preCheckData.itemName,
        isDeleteModalOpen: true,
      });
    } catch (error) {
      console.error("Error checking product dependencies:", error);
      // Fallback to simple delete modal if precheck fails
      this.setState({
        deletePreCheck: { hasLinkedRecords: false },
        productIdToDelete: productId,
        productNameToDelete: product ? product.name : "Product",
        isDeleteModalOpen: true,
      });
    }
  };

  // Handle archive action (hide from UI)
  handleArchive = async () => {
    const { productIdToDelete } = this.state;
    const apiRequests = useApiRequests();

    this.setState({ isDeleteLoading: true });

    try {
      const response = await apiRequests.deleteProductAction(productIdToDelete, 0); // 0 = Archive

      if (response.status === 200) {
        this.setState({
          isDeleteModalOpen: false,
          productIdToDelete: null,
          deletePreCheck: null,
          productNameToDelete: "",
          isDeleteLoading: false,
          isSuccessPopupOpen: true,
          successMessage: "Product archived successfully",
        });
        this.fetchProducts();
        setTimeout(() => {
          this.setState({ isSuccessPopupOpen: false, successMessage: "" });
        }, 5000);
      }
    } catch (error) {
      console.error("Error archiving product:", error);
      this.setState({ isDeleteLoading: false });
    }
  };

  // Handle permanent delete
  handlePermanentDelete = async () => {
    const { productIdToDelete, deletePreCheck } = this.state;
    const apiRequests = useApiRequests();

    this.setState({ isDeleteLoading: true });

    try {
      // If has linked records, use the new action endpoint
      if (deletePreCheck && deletePreCheck.hasLinkedRecords) {
        const response = await apiRequests.deleteProductAction(productIdToDelete, 1); // 1 = Delete

        if (response.status === 200) {
          this.setState({
            isDeleteModalOpen: false,
            productIdToDelete: null,
            deletePreCheck: null,
            productNameToDelete: "",
            isDeleteLoading: false,
            isSuccessPopupOpen: true,
            successMessage: "Product and linked records deleted successfully",
          });
          this.fetchProducts();
          setTimeout(() => {
            this.setState({ isSuccessPopupOpen: false, successMessage: "" });
          }, 5000);
        }
      } else {
        // No linked records, use simple delete
        const response = await apiRequests.deleteProductById(productIdToDelete);

        if (response.status === 200) {
          this.setState({
            isDeleteModalOpen: false,
            productIdToDelete: null,
            deletePreCheck: null,
            productNameToDelete: "",
            isDeleteLoading: false,
            isSuccessPopupOpen: true,
            successMessage: "Product deleted successfully",
          });
          this.fetchProducts();
          setTimeout(() => {
            this.setState({ isSuccessPopupOpen: false, successMessage: "" });
          }, 5000);
        }
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      this.setState({ isDeleteLoading: false });
    }
  };

  handleAvailabilityFilterChange(event) {
    this.setState({
      availabilityFilter: event.target.value,
      currentPage: 1,
    });
  }

  filterAndSortProducts(products) {
    const { userType, availabilityFilter } = this.state;

    // Combine all filter conditions into a single pass
    let filteredProducts = products.filter((product) => {
      // Exclude deleted products
      if (product.comment === "Deleted Product") return false;

      // For non-admin users, show only available products
      if (userType !== "1") {
        return product.isAvailable;
      }

      // For admin users, apply the availability filter
      if (availabilityFilter === "available") {
        return product.isAvailable;
      } else if (availabilityFilter === "unavailable") {
        return !product.isAvailable;
      }

      // If no availability filter, show all non-deleted products
      return true;
    });
    // Sort products: available first, unavailable last
    filteredProducts.sort((a, b) => {
      // Convert boolean to number: true (available) = 1, false (unavailable) = 0
      // Sort in descending order so available (1) comes before unavailable (0)
      return (b.isAvailable ? 1 : 0) - (a.isAvailable ? 1 : 0);
    });

    return filteredProducts;
  }

  render() {
    const {
      isAddModalOpen,
      isEditModalOpen,
      isDeleteModalOpen,
      isLoading,
      isDataLoading,
      isDeleteLoading,
      products,
      userType,
      selectedProduct,
      availabilityFilter,
      deletePreCheck,
      productNameToDelete,
      currentPage,
      pageSize,
    } = this.state;

    const filteredProducts = this.filterAndSortProducts(products);
    const pageItems = filteredProducts.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
      <>
        <PageTitle title="Visma Products" />
        <div className="col-10 m-auto">
          {isDataLoading ? (
            <LoadingSpinner message="Loading..." />
          ) : (
            <div className="shadow-50 d-flex flex-column justify-content-center align-items-center add-product">
              <div className="d-flex justify-content-between align-items-center mt-4 mb-4 flex-wrap products-header-container">
                {userType === "1" ? (
                  <>
                    <div className="products-header-spacer"></div>
                    <button
                      type="button"
                      className="btn btn-primary products-add-button-centered"
                      onClick={this.openAddProductModal}
                    >
                      Add New Product
                    </button>
                    <div className="d-flex align-items-center products-filter-container">
                      <label htmlFor="availabilityFilter" className="products-filter-label">
                        Filter by availability:
                      </label>
                      <select
                        id="availabilityFilter"
                        className="form-control products-filter-select"
                        value={availabilityFilter}
                        onChange={this.handleAvailabilityFilterChange}
                      >
                        <option value="all">All Products</option>
                        <option value="available">Available Only</option>
                        <option value="unavailable">Unavailable Only</option>
                      </select>
                    </div>
                  </>
                ) : (
                  null
                )}
              </div>
              {isLoading && !products ? (
                <LoadingSpinner />
              ) : (
                <div className="container-fluid products-content">
                  <div className="product-card-container row mt-32 justify-content-center">
                    {pageItems.map((product) => (
                      <ProductCardItem
                        key={product.id}
                        product={product}
                        userType={userType}
                        onEditProduct={this.openProductEditModal}
                        onDeleteProduct={this.handleDeleteClick}
                        onAddToCart={this.openAddToCartModal}
                        cartItems={this.context.cart.items}
                      />
                    ))}
                  </div>
                  <Pagination
                    currentPage={currentPage}
                    totalItems={filteredProducts.length}
                    pageSize={pageSize}
                    onPageChange={(page) => this.setState({ currentPage: page })}
                    onPageSizeChange={(size) => this.setState({ pageSize: size, currentPage: 1 })}
                    pageSizeOptions={[12, 24, 48]}
                    itemLabel="products"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {isAddModalOpen && (
          <ModalAddProduct
            isModalOpen={isAddModalOpen}
            fetchData={this.fetchProducts}
            isLoading={isLoading}
            handleModalClose={this.closeAllModals}
          />
        )}
        {isEditModalOpen && (
          <ModalEditProduct
            isModalOpen={isEditModalOpen}
            selectedProduct={selectedProduct}
            fetchData={this.fetchProducts}
            handleModalClose={this.closeAllModals}
          />
        )}
        {process.env.REACT_APP_FEATURE_DELETE_TYPE_VALUES_AND_PRODUCTS === "true" && isDeleteModalOpen && (
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={this.handleCancelAction}
            itemName={productNameToDelete}
            hasLinkedRecords={deletePreCheck?.hasLinkedRecords || false}
            transactionCount={deletePreCheck?.transactionCount || 0}
            requestCount={deletePreCheck?.requestCount || 0}
            onArchive={this.handleArchive}
            onDelete={this.handlePermanentDelete}
            isLoading={isDeleteLoading}
          />
        )}
        <AddToCartModal
          isOpen={this.state.addToCartModal.isOpen}
          productName={this.state.addToCartModal.productName}
          onConfirm={this.confirmAddToCart}
          onCancel={this.closeAddToCartModal}
        />
        <NotificationPopup
          message="Action was canceled"
          isPopupOpen={this.state.isCancelPopupOpen}
          type="error"
        />
        <NotificationPopup
          message={this.state.successMessage}
          isPopupOpen={this.state.isSuccessPopupOpen}
          type="success"
        />
      </>
    );
  }
}